// The onsite/components/back-in-stock/subscribe endpoint sends no CORS headers --
// Klaviyo's own widget only reaches it from inside a klaviyo.com iframe. The
// /client/ APIs are the browser-facing ones and feed the same Back in Stock flow.
const KLAVIYO_BIS_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_EVENTS_ENDPOINT = 'https://a.klaviyo.com/client/events/';
const KLAVIYO_API_REVISION = '2024-10-15';
// Klaviyo's catalog id for a Shopify variant.
const KLAVIYO_VARIANT_PREFIX = '$shopify:::$default:::';

class NotifyMe extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-notify-dialog]');
    this.form = this.querySelector('[data-notify-form]');
    if (!this.dialog || !this.form) return;

    this.emailInput = this.form.querySelector('input[type="email"]');
    this.submitButton = this.form.querySelector('[type="submit"]');
    this.errorMessage = this.querySelector('[data-notify-error]');
    this.errorText = this.querySelector('[data-notify-error-text]');
    this.successMessage = this.querySelector('[data-notify-success]');

    this.querySelector('[data-notify-open]')?.addEventListener('click', this.open.bind(this));
    this.querySelector('[data-notify-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', this.onDialogClick.bind(this));
    this.form.addEventListener('submit', this.onSubmit.bind(this));

    if (!this.dataset.account) {
      console.warn('[notify-me] No Klaviyo account id set, submissions will fail.');
    }
  }

  // <preorder-form> keeps data-variant current on variant change. The URL is the
  // fallback for a standalone dialog outside that wrapper.
  get variantId() {
    return this.dataset.variant || new URLSearchParams(window.location.search).get('variant');
  }

  // Shopify renders image_url as //shop/cdn/... and `shopify theme dev` rewrites it
  // again to /cdn/..., so resolve against the product URL, which is always absolute.
  // Klaviyo cannot render a relative image in an email.
  get productImage() {
    const raw = this.dataset.productImage;
    if (!raw) return '';
    try {
      return new URL(raw, this.dataset.productUrl || window.location.origin).href;
    } catch (error) {
      console.warn('[notify-me] Could not resolve image URL', raw, error);
      return '';
    }
  }

  open() {
    this.setState('idle');
    this.dialog.showModal();
    this.emailInput?.focus();
  }

  // showModal() stretches the dialog across the viewport, so a click that lands on
  // the dialog itself rather than the panel inside it is a backdrop click.
  onDialogClick(event) {
    if (event.target === this.dialog) this.dialog.close();
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.dataset.state === 'loading') return;

    const email = this.emailInput.value.trim();
    if (!email || !this.emailInput.checkValidity()) {
      this.showError(this.dataset.invalidMessage);
      this.emailInput.focus();
      return;
    }

    this.setState('loading');

    try {
      await this.subscribeToBackInStock(email);
      this.trackSignup(email);
      this.setState('success');
    } catch (error) {
      console.warn('[notify-me] Klaviyo subscription failed', error);
      this.showError(this.dataset.errorMessage);
    }
  }

  async subscribeToBackInStock(email) {
    const response = await fetch(
      `${KLAVIYO_BIS_ENDPOINT}?company_id=${encodeURIComponent(this.dataset.account)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_API_REVISION },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: { data: { type: 'profile', attributes: { email } } },
            },
            relationships: {
              variant: {
                data: { type: 'catalog-variant', id: KLAVIYO_VARIANT_PREFIX + this.variantId },
              },
            },
          },
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`${response.status} ${await response.text()}`.slice(0, 300));
    }
  }

  // Fire and forget: the shopper is already subscribed, so a failed analytics
  // event must never turn a successful signup into an error.
  trackSignup(email) {
    const metricName = this.dataset.eventName;
    if (!metricName) return;

    fetch(`${KLAVIYO_EVENTS_ENDPOINT}?company_id=${encodeURIComponent(this.dataset.account)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_API_REVISION },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            metric: { data: { type: 'metric', attributes: { name: metricName } } },
            profile: { data: { type: 'profile', attributes: { email } } },
            properties: {
              ProductName: this.dataset.productTitle,
              ProductID: this.dataset.productId,
              VariantID: this.variantId,
              Vendor: this.dataset.productVendor,
              ProductType: this.dataset.productType,
              ExpectedArrival: this.dataset.eta,
              URL: this.dataset.productUrl,
              ImageURL: this.productImage,
            },
          },
        },
      }),
    }).catch((error) => console.warn('[notify-me] Klaviyo event failed', error));
  }

  showError(message) {
    this.errorText.textContent = message;
    this.setState('error');
  }

  setState(state) {
    this.dataset.state = state;
    this.form.hidden = state === 'success';
    this.errorMessage.hidden = state !== 'error';
    this.successMessage.hidden = state !== 'success';
    // .loading drives the theme's shared button spinner from base.css.
    this.submitButton.classList.toggle('loading', state === 'loading');
    this.submitButton.disabled = state === 'loading';
  }
}

customElements.define('notify-me', NotifyMe);
